/**
 * Utility Functions
 * Tailwind CSS Class Merging + Helper Functions
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import i18n from '@/i18n';

const LOCALE_MAP: Record<string, string> = {
  de: 'de-DE',
  en: 'en-US',
  fr: 'fr-FR',
  it: 'it-IT',
  es: 'es-ES',
  nl: 'nl-NL',
};

export function getLocale(): string {
  return LOCALE_MAP[i18n.language] ?? 'de-DE';
}

/**
 * cn - Class Name Merge Utility
 *
 * Combines clsx and tailwind-merge for optimal class merging
 *
 * @example
 * ```tsx
 * cn('px-2 py-1', someCondition && 'bg-primary', 'px-4') // => 'py-1 bg-primary px-4'
 * ```
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format File Size
 *
 * @example
 * ```tsx
 * formatFileSize(1024) // => '1 KB'
 * formatFileSize(1048576) // => '1 MB'
 * ```
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Format Date
 *
 * @example
 * ```tsx
 * formatDate('2024-01-15T10:30:00Z') // => '15.01.2024'
 * ```
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(getLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/**
 * Format DateTime
 *
 * @example
 * ```tsx
 * formatDateTime('2024-01-15T10:30:00Z') // => '15.01.2024, 11:30'
 * ```
 */
export function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat(getLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * Format Currency (EUR)
 *
 * For monetary values like earnings, payouts, prices.
 *
 * @example
 * ```tsx
 * formatCurrency(1234.56) // => '1.234,56 €'
 * ```
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

/**
 * Format Credits
 *
 * For credit balance, costs, and transactions.
 *
 * @example
 * ```tsx
 * formatCredits(47233) // => '47.233 Credits'
 * formatCredits(100) // => '100 Credits'
 * formatCredits(100, true) // => '100 Cr' (short form)
 * ```
 */
export function formatCredits(amount: number, short: boolean = false): string {
  const formatted = new Intl.NumberFormat(getLocale()).format(amount);
  return short ? `${formatted} Cr` : `${formatted} Credits`;
}

/**
 * Format Credits with Sign
 *
 * For transactions showing + or - prefix.
 *
 * @example
 * ```tsx
 * formatCreditsWithSign(100) // => '+100 Credits'
 * formatCreditsWithSign(-100) // => '-100 Credits'
 * ```
 */
export function formatCreditsWithSign(amount: number): string {
  const absFormatted = new Intl.NumberFormat(getLocale()).format(Math.abs(amount));
  const sign = amount >= 0 ? '+' : '-';
  return `${sign}${absFormatted} Credits`;
}

/**
 * Format Number (locale-aware)
 *
 * Uses current i18n language for thousand separators.
 * DE: 3.641 | EN: 3,641 | FR: 3 641
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat(getLocale()).format(num);
}

/**
 * Truncate Text
 *
 * @example
 * ```tsx
 * truncate('Very long text here', 10) // => 'Very long...'
 * ```
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * Sleep / Delay
 *
 * @example
 * ```tsx
 * await sleep(1000); // Wait 1 second
 * ```
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Debounce Function
 *
 * @example
 * ```tsx
 * const debouncedSearch = debounce((query: string) => {
 *   // Search logic
 * }, 300);
 * ```
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Generate Random ID
 *
 * @example
 * ```tsx
 * randomId() // => 'a7b3c9d2'
 * ```
 */
export function randomId(): string {
  return Math.random().toString(36).substring(2, 10);
}

/**
 * Check if value is empty
 *
 * @example
 * ```tsx
 * isEmpty('') // => true
 * isEmpty([]) // => true
 * isEmpty({}) // => true
 * isEmpty(null) // => true
 * ```
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Count UTF-8 Bytes (for Amazon keyword validation)
 *
 * @example
 * ```tsx
 * countUtf8Bytes('hello') // => 5 bytes (1 byte each)
 * countUtf8Bytes('äöü')   // => 6 bytes (2 bytes each!)
 * ```
 */
export function countUtf8Bytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

/**
 * Remove duplicate keywords (case-insensitive)
 * Assumes keywords are space-separated
 *
 * @example
 * ```tsx
 * removeDuplicateKeywords('tshirt TShirt shirt') // => 'tshirt shirt'
 * removeDuplicateKeywords('äöü ÄÖÜ') // => 'äöü'
 * ```
 */
export function removeDuplicateKeywords(text: string): string {
  const keywords = text.trim().split(/\s+/);
  const seen = new Set<string>();
  const unique: string[] = [];

  for (const keyword of keywords) {
    const lowerCased = keyword.toLowerCase();
    if (!seen.has(lowerCased)) {
      seen.add(lowerCased);
      unique.push(keyword);
    }
  }

  return unique.join(' ');
}
